import React from 'react';

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useThemeContext } from '@infoods/lifebase-ui';

import classes from './ClientsGraph.module.scss';
import { ClientAnalyticsData } from '../../../../interfaces/entity-dashboard/ClientAnalyticsData';
import {
  getShowFirstAndLastLabelsParser,
  getChartBorder,
  getTooltipBaseOptions,
  getLabelForPeriod,
} from '../../../../../shared/features/charts';

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

type ClientsGraphProps = {
  customersGraph: ClientAnalyticsData['graphData'];
};

const ClientsGraph = ({ customersGraph }: ClientsGraphProps) => {
  const { palette } = useThemeContext();

  const borderPlugin = getChartBorder(palette);
  const tooltipBaseOptions = getTooltipBaseOptions(palette);
  const labels = customersGraph.data.map((item) =>
    getLabelForPeriod(item.periodFrom, item.periodTo, customersGraph.groupBy),
  );
  const newCustomersData = customersGraph.data.map((item) => item.newCustomers);
  const returningCustomersData = customersGraph.data.map(
    (item) => item.returningCustomers,
  );

  const fontCaption12Medium = {
    family: "'Lato', sans-serif",
    size: 12,
    weight: 500,
  } as const;

  const ticksOptions = {
    color: palette.neutral[600],
    font: fontCaption12Medium,
    maxRotation: 0,
  };

  return (
    <div className={classes.graph}>
      <Bar
        data={{
          labels,
          datasets: [
            {
              data: newCustomersData,
              label: 'New Customers',
              backgroundColor: palette.info[300],
              categoryPercentage: 0.6,
            },
            {
              data: returningCustomersData,
              label: 'Returning Customers',
              backgroundColor: palette.info[100],
              categoryPercentage: 0.6,
            },
          ],
        }}
        options={{
          animation: false,
          maintainAspectRatio: false,
          elements: {
            point: {
              radius: 0,
              hoverRadius: 0,
            },
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              ticks: {
                ...ticksOptions,
                autoSkip: false,
                padding: 0,
                callback: getShowFirstAndLastLabelsParser(
                  (value) => labels[value as number],
                ),
              },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
            },
            y: {
              position: 'right',
              beginAtZero: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
              ticks: {
                ...ticksOptions,
                maxTicksLimit: 5,
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...tooltipBaseOptions,
            },
          },
        }}
        plugins={[borderPlugin]}
      />
    </div>
  );
};

export { ClientsGraph };
